import React, { useCallback, useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import {
  adminLinkSessionToPath,
  adminUnlinkSessionFromPath,
  approveSessionPathLink,
  fetchSessionPathLinks,
  fetchTrainingPathsForAdmin,
  rejectSessionPathLink,
} from '../../services/training';

function pathShortTitle(title) {
  return title.replace(/^Camino de\s+/i, '');
}

export default function SessionCaminoLinksPanel({ sessionId, isAdmin }) {
  const [links, setLinks] = useState([]);
  const [paths, setPaths] = useState([]);
  const [addPathId, setAddPathId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [linkRows, pathRows] = await Promise.all([
        fetchSessionPathLinks(sessionId),
        fetchTrainingPathsForAdmin(),
      ]);
      setLinks(linkRows);
      setPaths(pathRows);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) return null;

  const approvedLinks = links.filter((l) => l.status === 'approved');
  const pendingLinks = links.filter((l) => l.status === 'pending');
  const linkedPathIds = new Set(links.map((l) => l.pathId));
  const availablePaths = paths.filter((p) => !linkedPathIds.has(p.id));

  const run = async (action, fn) => {
    setError('');
    setBusy(action);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo completar la acción');
    } finally {
      setBusy('');
    }
  };

  const handleAdd = () => {
    if (!addPathId) return;
    run(`add-${addPathId}`, async () => {
      await adminLinkSessionToPath(addPathId, sessionId);
      setAddPathId('');
    });
  };

  if (loading) return null;

  return (
    <div>
      <div className="p-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)]">
          Caminos
        </p>

        {error && (
          <div className="p-3 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
            {error}
          </div>
        )}

        {pendingLinks.map((link) => (
          <div
            key={`${link.pathId}-${link.sessionId}`}
            className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-amber-400/30 bg-amber-500/5"
          >
            <div className="text-sm text-[var(--text-main)]">
              <span className="font-bold">Camino sugerido:</span>{' '}
              {pathShortTitle(link.pathTitle)}
              {' '}
              <span className="text-amber-400 font-black uppercase text-[10px] tracking-widest">
                — Pendiente
              </span>
              {link.suggesterName && (
                <span className="block text-xs text-[var(--text-secondary)] mt-1">
                  Sugerido por {link.suggesterName}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => run(`approve-${link.pathId}`, () => approveSessionPathLink(link.pathId, link.sessionId))}
                className="flex items-center gap-1 px-3 py-2 rounded-full gfi-gradient-bg text-brand-navy text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                <SafeIcon name="Check" className="w-3 h-3" />
                Aprobar
              </button>
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => run(`reject-${link.pathId}`, () => rejectSessionPathLink(link.pathId, link.sessionId))}
                className="flex items-center gap-1 px-3 py-2 rounded-full border border-red-400/40 text-red-300 text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                <SafeIcon name="X" className="w-3 h-3" />
                Rechazar
              </button>
            </div>
          </div>
        ))}

        {approvedLinks.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-[var(--text-secondary)] font-bold uppercase tracking-widest">
              Enlaces aprobados
            </p>
            {approvedLinks.map((link) => (
              <div
                key={`${link.pathId}-approved`}
                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)]"
              >
                <span className="text-sm text-[var(--text-main)] font-medium">
                  {link.pathTitle}
                  {link.position != null && (
                    <span className="text-[var(--text-secondary)] text-xs ml-2">
                      #{link.position}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => run(`remove-${link.pathId}`, () => adminUnlinkSessionFromPath(link.pathId, link.sessionId))}
                  className="text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-300 disabled:opacity-50"
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>
        )}

        {availablePaths.length > 0 && (
          <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-[var(--border-subtle)]">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                Añadir a Camino
              </label>
              <select
                value={addPathId}
                onChange={(e) => setAddPathId(e.target.value)}
                disabled={Boolean(busy)}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-2 px-3 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-green"
              >
                <option value="">Seleccionar Camino…</option>
                {availablePaths.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={!addPathId || Boolean(busy)}
              onClick={handleAdd}
              className="px-4 py-2 rounded-full border border-[var(--border-subtle)] text-[10px] font-black uppercase tracking-widest text-[var(--text-main)] hover:border-brand-cyan disabled:opacity-50"
            >
              Añadir
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
